import {
  Box,
  Flex,
  FlexProps,
  HStack,
  Image,
  StackProps
} from "@chakra-ui/react"
import BoldThemeHeader from "@pathwright/ux/src/components/header/BoldThemeHeader"
import classNames from "classnames"
import { useEffect, useMemo } from "react"
import { SpaceIdentityFragment } from "@pathwright/graphql-client/src/generated"
import { SchoolGlobalThemeStyles } from "../../pathwright/GlobalSchoolThemeStyles"

// Get the shared class name for a space identity.
function getSpaceIdentityClassName(space: SpaceIdentityFragment) {
  return `space-identity-${space.id}`
}

// Currently a space's theme CSS is processed, nested under #root. We can't
// bleed a space's arbitrary CSS out to the global scope, so we need to replace
// the #root selector with one more specific (i.e. #root .space-identity-<id>).
function useSpaceIdentityCSS(space: SpaceIdentityFragment) {
  const rawCSS = space.theme?.processed_custom_css || ""
  const nextCSS = useMemo(
    () =>
      rawCSS.replace(/#root/g, `#root .${getSpaceIdentityClassName(space)}`),
    [rawCSS]
  )

  useEffect(() => {
    const dataAttribute = `data-space-identity-${space.id}-custom-css`
    const styleTag = document.querySelector(`style[${dataAttribute}]`)
    if (styleTag) {
      styleTag.innerHTML = nextCSS
    } else {
      const style = document.createElement("style")
      style.setAttribute(dataAttribute, "")
      style.innerHTML = nextCSS
      document.head.appendChild(style)
    }
  }, [nextCSS])
}

type SpaceIdentityWithNameProps = StackProps & {
  space: SpaceIdentityFragment
}

export function SpaceIdentityWithName({
  space,
  className,
  ...stackProps
}: SpaceIdentityWithNameProps) {
  return (
    <HStack
      className={classNames(getSpaceIdentityClassName(space), className)}
      w="full"
      {...stackProps}
    >
      <SpaceIdentity space={space} />
      <BoldThemeHeader
        as="h1"
        size="sm"
        m={0}
        lineClamp={1}
        color="black"
        textShadow="none"
      >
        {space?.name}
      </BoldThemeHeader>
    </HStack>
  )
}

type SpaceIdentityProps = FlexProps & {
  space: SpaceIdentityFragment
}

// NOTE: we aren't wrapping with SpaceTheme here, because it is actually quite slow in a list!
function SpaceIdentity({ space, className, ...props }: SpaceIdentityProps) {
  // Get the brand color for this space identity.
  // const brandColor = space.theme?.color_palette?._500

  // Apply any custom CSS for this space identity.
  useSpaceIdentityCSS(space)

  return (
    <>
      <Flex
        className={classNames(getSpaceIdentityClassName(space), className)}
        borderRadius="md"
        overflow="hidden"
        pos="relative"
        w="2rem"
        h="2rem"
        minW="2rem"
        minH="2rem"
        justifyContent="center"
        alignItems="center"
        bg={space.image_is_default ? `var(--school-main-color)` : "white"}
        {...props}
      >
        {space.image_is_default ? (
          <BoldThemeHeader as="span" size="md">
            {space.name.charAt(0).toUpperCase()}
          </BoldThemeHeader>
        ) : (
          <Image
            src={space.image!}
            alt={space.name}
            pos="absolute"
            inset={0}
            w="full"
            h="full"
          />
        )}
      </Flex>
      <SchoolGlobalThemeStyles
        $scope={`.${getSpaceIdentityClassName(space)}`}
        $theme={{
          primary_color: space.theme?.primary_color
            ? `#${space.theme?.primary_color}`
            : "#0d71fb",
          heading_font: space.theme?.heading_font
        }}
      />
    </>
  )
}

export default SpaceIdentity
